import React, { useCallback, useMemo } from "react"

import { TagDotsPreview } from "@/tags"
import { CopyableId, type ColumnDef, type SortDescriptor } from "@coval/ui"

import type { TestSetRecord } from "@/types/api/TestSetRecord"
import {
  formatFullTimestamp,
  formatRelativeTimestamp,
} from "@/utils/formatting/format-timestamp"

// Trimmed from the test sets list page's set to what a picker needs — no
// creator column, and the case count stays since it drives the run's size.
const NAME_COLUMN_WIDTH = 240
const NAME_COLUMN_MIN_WIDTH = 180
const DESCRIPTION_COLUMN_WIDTH = 300
const DESCRIPTION_MIN_WIDTH = 160
const CASES_COLUMN_WIDTH = 80
const DATE_COLUMN_WIDTH = 100
const TAGS_COLUMN_WIDTH = 110

const DEFAULT_SORT: SortDescriptor = {
  columnId: "last_updated_at",
  direction: "desc",
}

// Column model kept at module scope (deps passed in) so the JSX cells don't
// trip react/no-unstable-nested-components; the component memoizes the result.
function buildTestSetPickerColumns({
  tagColorMap,
}: {
  tagColorMap?: Record<string, string | null | undefined>
}): ColumnDef<TestSetRecord>[] {
  return [
    {
      id: "display_name",
      header: "Name",
      variant: "name",
      width: NAME_COLUMN_WIDTH,
      minWidth: NAME_COLUMN_MIN_WIDTH,
      sortable: true,
      cell: (testSet) => (
        <span className="min-w-0 truncate font-semibold">
          {testSet.display_name || testSet.id}
        </span>
      ),
    },
    {
      id: "description",
      header: "Description",
      width: DESCRIPTION_COLUMN_WIDTH,
      minWidth: DESCRIPTION_MIN_WIDTH,
      sortable: true,
      cell: (testSet) => (
        <span className="min-w-0 truncate text-coval-text-muted">
          {testSet.description || "No description"}
        </span>
      ),
    },
    {
      id: "test_case_count",
      header: "Cases",
      width: CASES_COLUMN_WIDTH,
      flex: false,
      sortable: true,
      cell: (testSet) => (
        <span className="text-coval-text-muted">
          {testSet.test_case_count || 0}
        </span>
      ),
    },
    {
      id: "last_updated_at",
      header: "Updated",
      width: DATE_COLUMN_WIDTH,
      sortable: true,
      cell: (testSet) => (
        <span
          className="whitespace-nowrap text-coval-text-muted"
          title={formatFullTimestamp(testSet.last_updated_at)}
        >
          {formatRelativeTimestamp(testSet.last_updated_at)}
        </span>
      ),
    },
    {
      id: "tags",
      header: "Tags",
      width: TAGS_COLUMN_WIDTH,
      align: "right",
      flex: false,
      cell: (testSet) => (
        <div className="flex min-w-0 justify-end">
          <TagDotsPreview tags={testSet.tags ?? []} tagColorMap={tagColorMap} />
        </div>
      ),
    },
    {
      id: "id",
      header: "ID",
      variant: "id",
      pinned: "right",
      cell: (testSet) => (
        <CopyableId id={testSet.id} label="Copy Test Set ID" />
      ),
    },
  ]
}

interface TestSetPickerDialogProps {
  isOpen: boolean
  onCancel: () => void
  onApply: () => void
  testSets: TestSetRecord[]
  isLoading?: boolean
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  allowMultiple?: boolean
  tagColorMap?: Record<string, string | null | undefined>
  banner?: React.ReactNode
  createAction?: React.ReactNode
}

export function useTestSetPickerDialogProps({
  isOpen,
  onCancel,
  onApply,
  testSets,
  isLoading = false,
  selectedIds,
  onSelectionChange,
  allowMultiple = true,
  tagColorMap,
  createAction,
  banner,
}: TestSetPickerDialogProps) {
  const columns = useMemo(
    () => buildTestSetPickerColumns({ tagColorMap }),
    [tagColorMap],
  )

  const matchesSearch = useCallback(
    (testSet: TestSetRecord, query: string) =>
      [
        testSet.display_name,
        testSet.id,
        testSet.description,
        ...(testSet.tags ?? []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query),
    [],
  )

  return {
    isOpen,
    onCancel,
    onApply,
    title: "Select Test Sets",
    items: testSets,
    columns,
    getItemId: (testSet: TestSetRecord) => testSet.id,
    getItemLabel: (testSet: TestSetRecord) =>
      testSet.display_name || testSet.id,
    matchesSearch,
    selectedIds,
    onSelectionChange,
    isLoading,
    allowMultiple,
    defaultSort: DEFAULT_SORT,
    rowAttributes: (testSet: TestSetRecord) => ({
      "data-testid": `test-set-option-${testSet.id}`,
      "data-test-set-name": testSet.display_name || undefined,
    }),
    searchPlaceholder: "Search test sets by name, description, or tag...",
    createAction,
    banner,
    emptyState: "No test sets yet",
    searchEmptyState: "No test sets match your search",
    "data-testid": "test-set-picker-dialog",
  }
}
